import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ExternalLink, User, FileText, Calendar, CreditCard, Mail, Phone } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value / 100);
}

const statusColors: Record<string, string> = {
  pending: 'bg-[hsl(var(--basic-100))] text-[hsl(var(--basic-700))]',
  paid: 'bg-[hsl(var(--accent-100))] text-[hsl(var(--accent-700))]',
  overdue: 'bg-[hsl(var(--careful-100))] text-[hsl(var(--careful-700))]',
  cancelled: 'bg-muted text-muted-foreground'
};

const statusLabels: Record<string, string> = {
  pending: 'Pendente',
  paid: 'Pago',
  overdue: 'Vencido',
  cancelled: 'Cancelado'
};

const FaturaDetalhe = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const fromPage = (location.state as { from?: string })?.from || '/faturas';

  const { data: invoice, isLoading } = useQuery({
    queryKey: ['invoice-detail', id],
    queryFn: async () => {
      if (!id) return null;

      const { data, error } = await supabase
        .from('invoices')
        .select(`
          *,
          customer:customers!inner(
            id, name, tax_id, email, phone, whatsapp,
            address, city, state, postal_code
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!id
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Carregando...</div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Fatura não encontrada</div>
      </div>
    );
  }

  const customer = invoice.customer as {
    id: string;
    name: string;
    tax_id: string;
    email: string | null;
    phone: string | null;
    whatsapp: string | null;
    address: string | null;
    city: string | null;
    state: string | null;
    postal_code: string | null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(fromPage)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-foreground">Detalhes da Fatura</h1>
          <p className="text-muted-foreground text-sm font-mono">{invoice.id}</p>
        </div>
        <Button variant="outline" className="gap-2" asChild>
          <Link to={`/checkout/${invoice.id}`} target="_blank">
            <ExternalLink className="h-4 w-4" />
            Abrir Checkout
          </Link>
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Invoice Info */}
        <div className="bg-card rounded-xl border p-6 space-y-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-lg font-semibold">Dados da Fatura</h2>
          </div>

          <div className="grid gap-4">
            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-muted-foreground">Status</span>
              <span className={`px-3 py-1 rounded text-xs font-bold uppercase ${statusColors[invoice.status]}`}>
                {statusLabels[invoice.status]}
              </span>
            </div>

            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-muted-foreground">Valor</span>
              <span className="text-2xl font-bold text-[hsl(var(--basic-900))]">
                {formatCurrency(invoice.amount)}
              </span>
            </div>

            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-muted-foreground flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Vencimento
              </span>
              <span className="font-medium">
                {format(new Date(invoice.due_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
              </span>
            </div>

            {invoice.paid_at && (
              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-muted-foreground flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Pago em
                </span>
                <span className="font-medium text-[hsl(var(--accent-600))]">
                  {format(new Date(invoice.paid_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                </span>
              </div>
            )}

            {invoice.payment_method && (
              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-muted-foreground">Forma de Pagamento</span>
                <span className="font-medium uppercase">{invoice.payment_method}</span>
              </div>
            )}

            {invoice.description && (
              <div className="py-3">
                <span className="text-muted-foreground block mb-2">Descrição</span>
                <p className="text-foreground">{invoice.description}</p>
              </div>
            )}

            {invoice.external_id && (
              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-muted-foreground">ID Externo</span>
                <span className="font-mono text-sm">{invoice.external_id}</span>
              </div>
            )}

            <div className="flex justify-between items-center py-3 text-sm text-muted-foreground">
              <span>Criada em</span>
              <span>{format(new Date(invoice.created_at), "dd/MM/yyyy HH:mm")}</span>
            </div>
          </div>
        </div>

        {/* Customer Info */}
        <div className="bg-card rounded-xl border p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <User className="h-5 w-5 text-primary" />
              </div>
              <h2 className="text-lg font-semibold">Dados do Pagador</h2>
            </div>
            <Button variant="outline" size="sm" asChild>
              <Link to={`/clientes/${customer.id}`} state={{ from: location.pathname }}>
                Ver Cliente
              </Link>
            </Button>
          </div>

          <div className="grid gap-4">
            <div className="py-3 border-b">
              <span className="text-muted-foreground block text-sm">Nome</span>
              <span className="font-semibold text-lg">{customer.name}</span>
            </div>

            <div className="py-3 border-b">
              <span className="text-muted-foreground block text-sm">CPF/CNPJ</span>
              <span className="font-mono">{customer.tax_id}</span>
            </div>

            {customer.email && (
              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-muted-foreground flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  E-mail
                </span>
                <a href={`mailto:${customer.email}`} className="text-primary hover:underline">
                  {customer.email}
                </a>
              </div>
            )}

            {customer.phone && (
              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-muted-foreground flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Telefone
                </span>
                <span>{customer.phone}</span>
              </div>
            )}

            {customer.whatsapp && (
              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-muted-foreground">WhatsApp</span>
                <span>{customer.whatsapp}</span>
              </div>
            )}

            {(customer.address || customer.city) && (
              <div className="py-3">
                <span className="text-muted-foreground block text-sm mb-1">Endereço</span>
                <p className="text-foreground">
                  {customer.address && <>{customer.address}<br /></>}
                  {customer.city && customer.state && (
                    <>{customer.city} - {customer.state}</>
                  )}
                  {customer.postal_code && <> • CEP: {customer.postal_code}</>}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FaturaDetalhe;
